import { toast } from '../../components';
import { useParams, useHistory, withRouter } from "react-router-dom";
import { usePermissions, useTranslate } from '../../customHooks';
import { BreadcrumbItem, Breadcrumbs, Button, Card, CardBody, Tab, TabList, TabPanel, Tabs, Title } from '@panwds/react-ui';
import { ArrowLeftIcon } from '@panwds/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FirewallGeneralInfo, FirewallOverview, FirewallEndpoints, FirewallEgressNat, FirewallLogProfile, FirewallSecurityPolicies, FirewallPrivateAccess, FirewallTier, FirewallAdvancedSettings } from './Components/Edit';
import { useGetFirewallByIdQuery } from '../../redux/services/firewalls-service';
import { useGetLinkAccountsQuery } from '../../redux/services/accounts-service';
import RulesList from '../rulesV2/RulesList';
import { includes, some } from 'lodash';
import { useGetFeatureFlagsQuery } from '../../redux/services/settings-service';
import { determinePolicyManaged, POLICY_MANAGER } from './firewallsUtil';
import styles from './FirewallEdit.module.scss';

const FirewallEdit = (props: any) => {
    const translate = useTranslate();
    const { firewallname } = useParams<{ firewallname: string }>();
    const [activeTabId, setActiveTabId] = useState<string>(sessionStorage.getItem('fwEditActiveTab') || 'overview')
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [accountList, setAccountsList] = useState<Record<string, unknown>[]>([]);
    const { rawPermissions } = usePermissions();
    const [canEdit, setCanEdit] = useState<boolean>(false);
    const [proxyAccountDetails, setProxyAccoutDetails] = useState<{ accountId: number, region: string }>({});

    const breadcrumbMapping = {
        ngfirewalls: `${translate('resources.firewallsV2.shortTitle')}`,
        [firewallname]: firewallname,
    };
    const region = useMemo(() => new URLSearchParams(location.search).get('region'), [location.search]) || undefined;

    const { data: firewall, error, isLoading: fwDetailsLoading, isFetching: isFwDetailsFetching, refetch } =
        useGetFirewallByIdQuery({ fwid: firewallname, region });

    useEffect(() => {
        setCanEdit(
            some(rawPermissions, { Policy: "GlobalFirewallAdmin" }) ||
            some(rawPermissions, obj =>
                includes(firewall?.Firewall?.ChangeProtection, `${obj.AccountId}/${obj.Policy}`)
            )
        );
    }, [firewall]);


    const handleTabChange = useCallback((tabId: string) => {
        sessionStorage.setItem('fwEditActiveTab', tabId);
        setActiveTabId(tabId);
        if (tabId !== "logProfile") {
            refetch();
        }
    }, [])

    const [stopFetching, setStopFetching] = useState(false);

    const { data: accounts, error: accountsError, isLoading } = useGetLinkAccountsQuery({
        nexttoken: nextToken || '',
        maxresults: 25,
        onboarded: true,
        describe: true,
    }, {
        skip: stopFetching,
    });

    useEffect(() => {
        if (accounts) {
            if (accounts?.items?.length > 0) {
                setAccountsList((prevItems) => [...prevItems, ...accounts.items]);
            }
            if (accounts?.serviceAccount) {
                setProxyAccoutDetails({ accountId: accounts?.serviceAccount, region: accounts.mpRegion || "us-east-1" });
            }
            // Continue fetching until nextToken is null
            if (accounts?.nexttoken) {
                setNextToken(accounts?.nexttoken);
            } else {
                setStopFetching(true); // Stop if nextToken is null
            }
        }
    }, [accounts]);

    useEffect(() => {
        if (nextToken !== null && !stopFetching) {
            setNextToken(nextToken); // Trigger the next query
        }
    }, [nextToken, stopFetching]);

    if (accountsError) {
        toast.error(accountsError?.error, { toastId: "firewall-edit-get-accounts" });
    }

    // Function to refresh the firewall data
    const refreshFirewallData = () => {
        refetch();
    };

    const { data: featureFlags, error: settingsError, isLoading: isSettingsLoading } =
        useGetFeatureFlagsQuery({ FeatureFlags: true, Region: region });

    const isPolicyManagerPanorama = determinePolicyManaged(firewall) === POLICY_MANAGER.PANORAMA;
    const isPolicyManagerRulestack = determinePolicyManaged(firewall) === POLICY_MANAGER.RULESTACK;

    const history = useHistory();

    return (
        <div className={styles.root}>
            <Card addClassName={styles.pageHeader}>
                    <div className={styles.breadcrumbs}>
                        <Breadcrumbs>
                            <BreadcrumbItem onClick={() => history.push('/ngfirewalls')}>
                                {translate('resources.firewalls.name')}
                            </BreadcrumbItem>
                            <BreadcrumbItem isCurrent>{firewallname}</BreadcrumbItem>
                        </Breadcrumbs>
                    </div>
                    <div className={styles.titleBar}>
                        <Button
                            aria-label="Back"
                            icon={<ArrowLeftIcon size="md" />}
                            onClick={() => history.push('/ngfirewalls')}
                            appearance="tertiary"
                        />
                        <Title level={1} size="sm">
                            {firewallname}
                        </Title>
                    </div>
            </Card>
            <Card addClassName={styles.tabsContainer}>
                <div className={styles.tabListWrapper}>
                    <Tabs orientation="vertical" addClassName={styles.customTabs}>
                        <TabList activeTabId={activeTabId} onActiveChange={nextTabId => handleTabChange(nextTabId)}>
                            <Tab id="overview">
                                {translate('resources.firewallsV2.overview')}
                            </Tab>
                            {isPolicyManagerRulestack && <Tab id="rules">
                                {translate('resources.firewallsV2.rules')}
                            </Tab>}
                            <Tab id="general">
                                {translate('resources.firewallsV2.generalInfo')}
                            </Tab>
                            <Tab id="tier">
                                {translate('resources.firewallsV2.tier.title')}
                            </Tab>
                            <Tab id="policies">
                                {translate('resources.firewallsV2.policyManager')}
                            </Tab>
                            <Tab id="egress">
                                {translate('resources.firewallsV2.egressNat')}
                            </Tab>
                            {(featureFlags?.PrivateAccess == true && isPolicyManagerPanorama) && <Tab id="privateAccess">
                                {translate('resources.firewallsV2.privateAccess.title')}
                            </Tab>}
                            <Tab id="endpoints">
                                {translate('resources.firewallsV2.endpointManagement.title')}
                            </Tab>
                            <Tab id="logProfile">
                                {translate('resources.firewallsV2.logMetrics.title')}
                            </Tab>
                            {featureFlags?.GwlbTcpIdleTimeout && <Tab id="advancedSettings">
                                {translate('resources.firewallsV2.advancedSettings')}
                            </Tab>}
                        </TabList>
                    </Tabs>
                </div>
                <div className={styles.tabPanels}>
                    <div className={styles.tabPanelContent}>
                        <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="overview">
                            <FirewallOverview fwid={firewallname} region={region} key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} firewallData={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching || isSettingsLoading} refreshFirewallData={refreshFirewallData} handleTabChange={handleTabChange} featureFlags={featureFlags} />
                        </TabPanel>
                        {isPolicyManagerRulestack && <TabPanel addClassName={styles.tableBackground} activeTabId={activeTabId} forTabId="rules">
                            <RulesList key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} data={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching} />
                        </TabPanel>}
                        <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="general">
                            <FirewallGeneralInfo canEdit={canEdit} key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} firewallData={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching || isSettingsLoading} refreshFirewallData={refreshFirewallData} />
                        </TabPanel>
                        <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="tier">
                            <FirewallTier canEdit={canEdit} key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} firewallData={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching} refreshFirewallData={refreshFirewallData} />
                        </TabPanel>
                        <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="policies">
                            <FirewallSecurityPolicies canEdit={canEdit} key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} firewallData={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching} refreshFirewallData={refreshFirewallData} />
                        </TabPanel>
                        <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="egress">
                            <FirewallEgressNat canEdit={canEdit} key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} firewallData={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching} refreshFirewallData={refreshFirewallData} featureFlags={featureFlags} />
                        </TabPanel>
                        {(featureFlags?.PrivateAccess == true && isPolicyManagerPanorama) && <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="privateAccess">
                            <FirewallPrivateAccess canEdit={canEdit} key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} accounts={accountList} firewallData={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching} refreshFirewallData={refreshFirewallData} />
                        </TabPanel>}
                        <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="endpoints">
                            <FirewallEndpoints canEdit={canEdit} key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} accounts={accountList} firewallData={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching} refreshFirewallData={refreshFirewallData} />
                        </TabPanel>
                        <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="logProfile">
                            <FirewallLogProfile canEdit={canEdit} accounts={accountList} proxyAccountDetails={proxyAccountDetails} featureFlags={featureFlags} />
                        </TabPanel>
                        {featureFlags?.GwlbTcpIdleTimeout && <TabPanel addClassName={styles.whiteBackground} activeTabId={activeTabId} forTabId="advancedSettings">
                            <FirewallAdvancedSettings canEdit={canEdit} key={firewall?.Firewall?.UpdateToken + firewall?.Firewall?.DeploymentUpdateToken} firewallData={firewall} isLoading={fwDetailsLoading || isFwDetailsFetching || isSettingsLoading} refreshFirewallData={refreshFirewallData} />
                        </TabPanel>}
                    </div>
                </div>
            </Card>
        </div>
    );
};
export default withRouter(FirewallEdit);
